import type { AnalyticsDto, ConstraintViolationDto, RiskLevel, RocketDto, VehiclePerformanceDto } from '@trajectory/contracts';

export interface SimOutcome {
  deltaVExpendedMetersPerSecond: number;
  reachedOrbit: boolean;
  reachedDestination?: boolean;
  destinationBodyId?: string;
  terminationReason: string;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Mission analytics computed client-side from data already loaded (rocket
 * performance, the mission's own ascent simulation once one has been run).
 * The cost model is a deliberately rough per-kilogram heuristic, not a
 * costing tool — there is no real launch-cost dataset behind it, and that is
 * disclosed in `docs/`, not hidden behind an falsely precise-looking number.
 */
export function computeAnalytics(rocket: RocketDto, performance: VehiclePerformanceDto, sim: SimOutcome | null): AnalyticsDto {
  const propellantMassKg = performance.stages.reduce((sum, stage) => sum + stage.propellantMassKg, 0);
  const fuelEfficiency = propellantMassKg > 0 ? performance.payloadMassKg / propellantMassKg : 0;

  let estimatedCostUsd = 0;
  for (const stage of rocket.stages) {
    estimatedCostUsd += (stage.engine.unitCostUsd ?? 0) * stage.engineCount;
    if (stage.boosters) {
      estimatedCostUsd += (stage.boosters.engine.unitCostUsd ?? 0) * stage.boosters.enginesPerBooster * stage.boosters.count;
    }
  }
  // A rocket with no priced engines in the catalogue would otherwise cost
  // "$0" — floor it at a rough $500/kg of gross lift-off mass instead.
  estimatedCostUsd = Math.max(estimatedCostUsd, performance.totalMassKg * 500);
  const costPerKilogramUsd = performance.payloadMassKg > 0 ? estimatedCostUsd / performance.payloadMassKg : 0;

  // A measured delta-v expenditure from an actual run is ground truth; absent
  // that, ~9.4 km/s is the standard rule-of-thumb budget for reaching a
  // low-Earth parking orbit from the surface (vis-viva ~7.8 km/s plus ~1.5-2
  // km/s of drag and gravity losses).
  const requiredDeltaV = sim ? sim.deltaVExpendedMetersPerSecond : 9400;
  const deltaVMargin = performance.totalDeltaVMetersPerSecond - requiredDeltaV;
  const payloadEfficiency = performance.totalMassKg > 0 ? performance.payloadMassKg / performance.totalMassKg : 0;
  const launchSuccessProbability = rocket.demonstratedReliability ?? (performance.warnings.length === 0 ? 0.95 : 0.85);

  const violations: ConstraintViolationDto[] = performance.warnings.map((message) => ({
    code: 'vehicle-performance',
    severity: 'warning',
    message,
  }));
  if (deltaVMargin < 0) {
    violations.push({
      code: 'delta-v-shortfall',
      severity: 'error',
      message: `Delta-v margin is negative by ${Math.abs(deltaVMargin).toFixed(0)} m/s`,
      value: performance.totalDeltaVMetersPerSecond,
      limit: requiredDeltaV,
    });
  }
  if (!performance.orbitCapable) {
    violations.push({ code: 'not-orbit-capable', severity: 'error', message: 'Vehicle is not orbit-capable at this payload mass.' });
  }
  if (sim && !sim.reachedOrbit) {
    violations.push({
      code: 'sim-did-not-reach-orbit',
      severity: 'warning',
      message: `The last simulation ended in: ${sim.terminationReason}`,
    });
  }
  if (sim?.reachedDestination === false && sim.terminationReason === 'transfer-infeasible') {
    violations.push({
      code: 'sim-did-not-reach-destination',
      severity: 'error',
      message: `The configured vehicle/trajectory did not reach ${sim.destinationBodyId ?? 'the mission destination'}. Review the transfer event for the exact delta-v or targeting shortfall.`,
    });
  }

  const riskLevel: RiskLevel = violations.some((v) => v.severity === 'error')
    ? 'high'
    : deltaVMargin < 200
      ? 'elevated'
      : violations.length > 0
        ? 'moderate'
        : 'low';

  const marginScore = clamp01(deltaVMargin / 1000 + 0.5) * 40;
  const efficiencyScore = clamp01(payloadEfficiency * 20) * 30;
  const reliabilityScore = launchSuccessProbability * 30;

  return {
    fuelEfficiency,
    estimatedCostUsd,
    costPerKilogramUsd,
    launchSuccessProbability,
    performanceScore: Math.round(marginScore + efficiencyScore + reliabilityScore),
    deltaVMarginMetersPerSecond: deltaVMargin,
    payloadEfficiency,
    riskLevel,
    riskFactors: violations.map((v) => v.message),
    violations,
  };
}
